import { useState, useEffect, useCallback } from 'react';
import { useCanvas } from '../../contexts/CanvasContext';
import { useDesignerStore } from '../../stores';
import { usePricingStore } from '../../stores/pricingStore';
import { apiClient } from '../../utils/api';
import type { ProductVariant } from '../../types/pricing';
import type { Template } from '../../types/template';
import styles from './VariantSelector.module.css';

// Import fabric for side effects
import 'fabric';

interface VariantSelectorProps {
  productId: string;
}

interface VariantsResponse {
  success: boolean;
  variants?: ProductVariant[];
  error?: string;
}

interface TemplateResponse {
  success: boolean;
  template?: Template;
  error?: string;
}

export const VariantSelector = ({ productId }: VariantSelectorProps) => {
  const { canvas } = useCanvas();
  const { variantId: urlVariantId, setLoading } = useDesignerStore();
  const { variants, setVariants, selectVariant, selectedVariantId } = usePricingStore();
  const [isLoading, setIsLoading] = useState(false);

  // Load variants on mount
  useEffect(() => {
    if (productId) {
      loadVariants();
    }
  }, [productId]);

  const loadVariants = async () => {
    setIsLoading(true);

    try {
      const response = await apiClient.get<VariantsResponse>(
        `/api/products/${productId}/variants`
      );

      if (response.success && response.data?.variants && response.data.variants.length > 0) {
        setVariants(response.data.variants);

        // Pre-select variant from URL if available
        if (urlVariantId) {
          const variant = response.data.variants.find(
            (v) => v.id.toString() === urlVariantId
          );
          if (variant) {
            selectVariant(variant.id);
          }
        } else if (response.data.variants?.[0]) {
          // Auto-select first variant
          selectVariant(response.data.variants[0].id);
        }
      }
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = useCallback(async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newVariantId = e.target.value;
    if (!newVariantId || !canvas || newVariantId === selectedVariantId?.toString()) return;

    setLoading(true, 'Switching variant...');

    try {
      // Store customer designs (preserve user's work)
      const customerObjects = canvas.getObjects().filter(
        (obj) => !obj.isBaseImage && !obj.isMaskOverlay && !obj.isGuide && !obj.isMockup
      );

      // Save customer object data
      const savedObjects = customerObjects.map((obj) => obj.toJSON([
        'id', 'uploadedUrl', 'serverUrl', 'laserConverted', 'convertedFrom'
      ]));

      // Remove old template layers (base image and masks)
      const templateLayers = canvas.getObjects().filter(
        (obj) => obj.isBaseImage || obj.isMaskOverlay || obj.isGuide
      );
      templateLayers.forEach((obj) => canvas.remove(obj));

      // Load new variant template
      const response = await apiClient.get<TemplateResponse>(
        `/api/product/${productId}/template?variantId=${newVariantId}`
      );

      if (response.success && response.data?.template) {
        const template = response.data.template;

        // Load base image
        if (template.baseImage) {
          await loadTemplateImage(template.baseImage, { isBaseImage: true, isMockup: true });
        }

        // Load alpha mask
        if (template.alphaMask) {
          await loadTemplateImage(template.alphaMask, { isMaskOverlay: true, isMockup: true });
        }

        // Load clipped mask if different
        if (template.clippedMask && template.clippedMask !== template.alphaMask) {
          await loadTemplateImage(template.clippedMask, { isMaskOverlay: true, isMockup: true });
        }

        // Restore customer designs on top
        const f = window.fabric;
        savedObjects.forEach((objData) => {
          f.util.enlivenObjects([objData], (objects: fabric.Object[]) => {
            objects.forEach((obj) => canvas.add(obj));
          }, '');
        });

        // Ensure masks stay on top
        canvas.getObjects().forEach((obj) => {
          if (obj.isMaskOverlay) {
            canvas.bringToFront(obj);
          }
        });

        canvas.renderAll();
      }

      // Update store and URL
      selectVariant(parseInt(newVariantId) || newVariantId);
      const url = new URL(window.location.href);
      url.searchParams.set('variantId', newVariantId);
      window.history.replaceState({}, '', url.toString());

    } catch (err) {
      console.error('Variant switch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [canvas, productId, selectedVariantId, selectVariant, setLoading]);

  const loadTemplateImage = (url: string, options: Partial<fabric.Image>): Promise<void> => {
    return new Promise((resolve, reject) => {
      const f = window.fabric;

      f.Image.fromURL(url, (img) => {
        if (!img || !canvas) {
          reject(new Error('Failed to load image'));
          return;
        }

        img.set({
          ...options,
          left: 0,
          top: 0,
          selectable: false,
          evented: false,
        });

        // Scale to canvas
        const scaleX = 800 / (img.width || 800);
        const scaleY = 800 / (img.height || 800);
        img.scale(Math.min(scaleX, scaleY));

        canvas.add(img);

        if (options.isBaseImage) {
          canvas.sendToBack(img);
        } else if (options.isMaskOverlay) {
          canvas.bringToFront(img);
        }

        resolve();
      }, { crossOrigin: 'anonymous' });
    });
  };

  if (variants.length === 0) {
    return null;
  }

  return (
    <div className={styles.variantSelector}>
      <label className={styles.label} htmlFor="variant-select">
        Options
      </label>
      <select
        id="variant-select"
        className={styles.select}
        value={selectedVariantId?.toString() || ''}
        onChange={handleChange}
        disabled={isLoading}
      >
        <option value="">Select options...</option>
        {variants.map((variant) => (
          <option key={variant.id} value={variant.id}>
            {variant.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default VariantSelector;
